/* ============================================================
   Back in stock: the "Let me know when it's back" form inside the
   sold-out modal on the product page.

   Posts straight to Klaviyo's client Back in Stock API with the
   public site key (no app proxy, same pattern as fb-next-register).
   Each submit records a "Subscribed to Back in Stock" event against
   the selected variant; the Klaviyo back-in-stock flow then holds
   the profile until Shopify reports the variant restocked.

   SMS is opt-in. A ticked consent box first records SMS marketing
   consent through the client subscriptions API, into a dedicated
   list (Klaviyo will not text a restock alert without consent, and
   the endpoint requires a list). Then the back-in-stock
   subscription asks for both channels. If consent fails, the
   subscription still goes through for email.

   Once the alert is saved, the shopper also joins "Welcome List 20%
   More Stems" with email marketing consent (disclosed under the
   button), which starts the welcome flows. Best effort only.
   ============================================================ */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2026-01-15';
  var CLIENT_API = 'https://a.klaviyo.com/client/';

  // Australian mobiles are the common case, typed as 04xx xxx xxx.
  // Klaviyo wants E.164, so 04… becomes +614…; anything already
  // international is kept. Landlines cannot take a text, so a local
  // number that is not 04 is rejected rather than silently dropped.
  function normalisePhone(raw) {
    var digits = String(raw || '').replace(/[\s().-]/g, '');
    if (!digits) return null;
    if (/^\+/.test(digits)) {
      return /^\+[1-9]\d{7,14}$/.test(digits) ? digits : null;
    }
    if (/^0?4\d{8}$/.test(digits)) {
      return '+61' + digits.replace(/^0/, '');
    }
    if (/^614\d{8}$/.test(digits)) {
      return '+' + digits;
    }
    return null;
  }

  function post(path, companyId, payload) {
    return fetch(CLIENT_API + path + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Klaviyo ' + path + ' responded ' + response.status);
      }
      return response;
    });
  }

  // Klaviyo's client subscriptions endpoint rejects a request that names
  // no list (400 "'list' is a required relationship"). The list is a
  // dedicated one with no flow attached, so consenting here does not also
  // start the SMS welcome series that the main SMS list triggers.
  function smsConsentPayload(email, phone, listId) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock alert',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                phone_number: phone,
                subscriptions: {
                  sms: { marketing: { consent: 'SUBSCRIBED' } }
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };
  }

  // Joins "Welcome List 20% More Stems" with email marketing consent. That
  // consent is what starts the live welcome flows ("Subscribed to Email
  // Marketing"), and the form says so under the button.
  function welcomePayload(email, listId) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock alert',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } }
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };
  }

  function backInStockPayload(email, phone, channels, variantId) {
    var profile = { email: email };
    if (phone) profile.phone_number = phone;
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: profile } },
          channels: channels
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    };
  }

  // The variant the shopper is looking at lives in the add-to-cart
  // form's hidden id input, which product-variant.js keeps current.
  // The modal's own data attribute is the server-rendered fallback.
  function selectedVariantId(form) {
    var sectionId = form.dataset.section;
    var productForm = sectionId ? document.getElementById('product-form-' + sectionId) : null;
    var input = productForm ? productForm.querySelector('input[name="id"]') : null;
    if (input && input.value) return String(input.value);
    return form.dataset.variantId || '';
  }

  function initForm(form) {
    if (!form || form.dataset.fbBisReady === 'true') return;
    form.dataset.fbBisReady = 'true';

    var companyId = form.dataset.companyId;
    var submitBtn = form.querySelector('[data-fb-bis-submit]');
    var errorBox = form.querySelector('[data-fb-bis-error]');
    var success = form.parentNode.querySelector('[data-fb-bis-success]');
    var successSms = success ? success.querySelector('[data-fb-bis-success-sms]') : null;
    var successSmsFailed = success ? success.querySelector('[data-fb-bis-success-sms-failed]') : null;
    var smsListId = form.dataset.smsListId;
    var welcomeListId = form.dataset.welcomeListId;
    var submitLabel = submitBtn ? submitBtn.textContent : '';
    var strings = {
      generic: form.dataset.errorGeneric || 'Something went wrong and we could not save that. Please try again.',
      phone: form.dataset.errorPhone || 'That mobile number does not look right.',
      needsPhone: form.dataset.errorNeedsPhone || 'Add your mobile number for a text, or untick the box.'
    };

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      hideError();

      var honeypot = form.querySelector('input[name="website"]');
      if (honeypot && honeypot.value) {
        showSuccess(false);
        return;
      }

      if (!form.reportValidity()) return;

      var email = valueOf('email');
      var rawPhone = valueOf('phone');
      var consent = form.querySelector('input[name="sms_consent"]');
      var wantsSms = !!(consent && consent.checked);
      var variantId = selectedVariantId(form);

      if (!variantId) {
        showError(strings.generic);
        return;
      }
      if (wantsSms && !rawPhone) {
        showError(strings.needsPhone);
        return;
      }

      var phone = rawPhone ? normalisePhone(rawPhone) : null;
      if (rawPhone && !phone) {
        showError(strings.phone);
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'One moment…';
      }

      // SMS consent comes first because Klaviyo will not text without it.
      // If that step fails, the shopper still gets the email alert: a
      // broken text opt-in must never cost them the thing they came for.
      var consentStep = wantsSms && phone && smsListId
        ? post('subscriptions/', companyId, smsConsentPayload(email, phone, smsListId)).then(
            function () { return true; },
            function () { return false; }
          )
        : Promise.resolve(false);

      consentStep
        .then(function (smsOk) {
          var channels = smsOk ? ['EMAIL', 'SMS'] : ['EMAIL'];
          return post(
            'back-in-stock-subscriptions/',
            companyId,
            backInStockPayload(email, smsOk ? phone : null, channels, variantId)
          ).then(function () {
            return smsOk;
          });
        })
        .then(function (smsOk) {
          showSuccess(smsOk, wantsSms && !smsOk);
          track(variantId, smsOk);
          // Best effort: the alert is saved, so a failed list signup is not
          // worth an error the shopper cannot act on.
          if (welcomeListId) {
            post('subscriptions/', companyId, welcomePayload(email, welcomeListId)).catch(function () {});
          }
        })
        .catch(function () {
          showError(strings.generic);
          resetButton();
        });
    });

    function valueOf(name) {
      var field = form.querySelector('[name="' + name + '"]');
      return field ? field.value.trim() : '';
    }

    function resetButton() {
      if (!submitBtn) return;
      submitBtn.disabled = false;
      submitBtn.textContent = submitLabel;
    }

    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = false;
    }

    function hideError() {
      if (!errorBox) return;
      errorBox.hidden = true;
    }

    function showSuccess(withSms, smsFailed) {
      form.hidden = true;
      if (!success) return;
      success.hidden = false;
      if (successSms) successSms.hidden = !withSms;
      if (successSmsFailed) successSmsFailed.hidden = !smsFailed;
      var heading = success.querySelector('[data-fb-bis-success-heading]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    function track(variantId, withSms) {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      window.posthog.capture('back_in_stock_subscribed', {
        variant_id: variantId,
        sms: withSms
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-fb-bis-form]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-fb-bis-form]').forEach(function (form) {
      delete form.dataset.fbBisReady;
      initForm(form);
    });
  });

  // Exposed for the node test; nothing on the page reads it.
  window.fbBackInStock = {
    normalisePhone: normalisePhone,
    smsConsentPayload: smsConsentPayload,
    welcomePayload: welcomePayload,
    backInStockPayload: backInStockPayload
  };
})();
